import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './SkillsBouncing.css';

const skills = [
  {
    name: 'HTML5',
    img: process.env.PUBLIC_URL + '/SkillsBouncing/html.svg',
    description: "HTML5 - Langage de balisage pour structurer le contenu web"
  },
  {
    name: 'CSS3',
    img: process.env.PUBLIC_URL + '/SkillsBouncing/css.svg',
    description: "CSS3 - Langage de style pour la mise en page et le design web"
  },
  {
    name: 'JavaScript',
    img: process.env.PUBLIC_URL + '/SkillsBouncing/javascript.svg',
    description: "JavaScript - Langage de programmation pour l'interactivité web"
  },
  {
    name: 'React',
    img: process.env.PUBLIC_URL + '/SkillsBouncing/react.svg',
    description: "React - Bibliothèque JavaScript pour construire des interfaces utilisateur"
  },
  {
    name: 'Sass',
    img: process.env.PUBLIC_URL + '/SkillsBouncing/sass.svg',
    description: "Sass - Préprocesseur CSS qui ajoute des fonctionnalités avancées"
  },
  {
    name: 'npm',
    img: process.env.PUBLIC_URL + '/SkillsBouncing/npm.svg',
    description: "npm - Gestionnaire de paquets pour JavaScript"
  },
  {
    name: 'PHP',
    img: process.env.PUBLIC_URL + '/SkillsBouncing/php.svg',
    description: "PHP - Langage de programmation pour le développement web côté serveur"
  },
  {
    name: 'Figma',
    img: process.env.PUBLIC_URL + '/SkillsBouncing/figma.svg',
    description: "Figma - Outil de conception d'interface utilisateur en ligne"
  },
  {
    name: 'Vercel',
    img: process.env.PUBLIC_URL + '/SkillsBouncing/vercel.svg',
    description: "Vercel - Plateforme de déploiement et d'hébergement pour applications web"
  },
  {
    name: 'Redux',
    img: process.env.PUBLIC_URL + '/SkillsBouncing/redux.svg',
    description: "Redux - Bibliothèque de gestion d'état pour applications JavaScript"
  },
  {
    name: 'Symfony',
    img: process.env.PUBLIC_URL + '/SkillsBouncing/symfony.svg',
    description: "Symfony - Framework PHP pour développer des applications web"
  },
  {
    name: 'SEO',
    img: process.env.PUBLIC_URL + '/SkillsBouncing/seo.svg',
    description: "SEO - Optimisation pour les moteurs de recherche"
  },
  {
    name: 'Scrum',
    img: process.env.PUBLIC_URL + '/SkillsBouncing/scrum.svg',
    description: "Scrum - Méthode agile de gestion de projet"
  },
  {
    name: 'Kanban',
    img: process.env.PUBLIC_URL + '/SkillsBouncing/kanban.svg',
    description: "Kanban - Système de gestion de flux pour visualiser le travail"
  },
  {
    name: 'GitHub',
    img: process.env.PUBLIC_URL + '/SkillsBouncing/github.svg',
    description: "GitHub - Plateforme de développement collaboratif et de gestion de versions"
  },
  {
    name: 'Debug',
    img: process.env.PUBLIC_URL + '/SkillsBouncing/debug.svg',
    description: "Debug - Techniques d'identification et résolution de problèmes dans le code"
  },
];

const getRandom = (min, max) => Math.random() * (max - min) + min;

const SkillsBouncing = () => {
  const containerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [positions, setPositions] = useState([]);
  const [velocities, setVelocities] = useState([]);
  const [activeIdx, setActiveIdx] = useState(null);
  const [showPrompt, setShowPrompt] = useState(true);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (!containerRef.current) return;
    const { offsetWidth: width, offsetHeight: height } = containerRef.current;
    setContainerSize({ width, height });
    setPositions(skills.map(() => ({
      x: getRandom(0, width - 60),
      y: getRandom(0, height - 60)
    })));
    setVelocities(skills.map(() => ({
      vx: getRandom(0.3, 0.8) * (Math.random() < 0.5 ? 1 : -1), 
      vy: getRandom(0.3, 0.8) * (Math.random() < 0.5 ? 1 : -1)  
    })));
    // eslint-disable-next-line
  }, [containerRef.current]);

  useEffect(() => {
    function animate() {
      setPositions(prev => prev.map((pos, i) => {
        if (activeIdx === i) return pos; 
        let { x, y } = pos;
        let { vx, vy } = velocities[i] || { vx: 0.5, vy: 0.5 };
        x += vx;
        y += vy;
        if (x <= 0 || x >= containerSize.width - 60) vx *= -1;
        if (y <= 0 || y >= containerSize.height - 60) vy *= -1;
        velocities[i] = { vx, vy };
        return { x: Math.max(0, Math.min(x, containerSize.width - 60)), y: Math.max(0, Math.min(y, containerSize.height - 60)) };
      }));
      animationFrameRef.current = requestAnimationFrame(animate);
    }
    if (positions.length && velocities.length) {
      animationFrameRef.current = requestAnimationFrame(animate);
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
    // eslint-disable-next-line
  }, [positions.length, velocities.length, containerSize, activeIdx]);

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current) return;
      setContainerSize({
        width: containerRef.current.offsetWidth,
        height: containerRef.current.offsetHeight
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (!containerRef.current.contains(e.target)) {
        setActiveIdx(null);
        setShowPrompt(true);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const createLogoExplosion = (x, y) => {
    const colors = ['#d1a6c6', '#00d4ff', '#7c3aed'];
    const newParticles = Array.from({ length: 8 }, (_, i) => {
      const angle = (i / 8) * Math.PI * 2;
      const speed = 2 + Math.random() * 3;
      return {
        id: Date.now() + i,
        x: x + 30,
        y: y + 30,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
        size: 2 + Math.random() * 3,
        color: colors[Math.floor(Math.random() * colors.length)]
      };
    });
    
    setParticles(prev => [...prev, ...newParticles]);
    
    setTimeout(() => {
      setParticles(prev => prev.filter(p => !newParticles.some(np => np.id === p.id)));
    }, 1000);
  };

  useEffect(() => {
    if (particles.length === 0) return;
    
    const animate = () => {
      setParticles(prev => prev.map(particle => ({
        ...particle,
        x: particle.x + particle.vx,
        y: particle.y + particle.vy,
        life: particle.life - 0.02,
        vx: particle.vx * 0.98,
        vy: particle.vy * 0.98 + 0.1
      })).filter(p => p.life > 0));
    };
    
    const interval = setInterval(animate, 32); 
    return () => clearInterval(interval);
  }, [particles.length]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.08,
        delayChildren: 0.2 
      }
    }
  };

  const logoVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
      transition: { 
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="compétences" className="skills-bouncing">
      <motion.div 
        className="skills-header"
        initial={{ y: -50, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="skills-title">Compétences</h2>
      </motion.div>
      
      <motion.div 
        className="skills-logos" 
        ref={containerRef}
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        {skills.map((skill, i) => (
          <motion.div
            className="logo-container"
            key={skill.name}
            variants={logoVariants}
            whileHover={{ scale: 1.05 }}
            style={{
              left: positions[i]?.x || 0,
              top: positions[i]?.y || 0,
              zIndex: activeIdx === i ? 20 : 10,
              scale: activeIdx === i ? 1.1 : 1
            }}
          >
            <img
              className={`logo-floating${activeIdx === i ? ' active' : ''}`}
              src={skill.img}
              alt={skill.name}
              onClick={(e) => {
                const rect = e.target.getBoundingClientRect();
                const containerRect = containerRef.current.getBoundingClientRect();
                createLogoExplosion(
                  rect.left - containerRect.left,
                  rect.top - containerRect.top
                );
                setActiveIdx(activeIdx === i ? null : i);
                setShowPrompt(activeIdx === i);
              }}
              data-description={skill.description}
              draggable={false}
            />
          </motion.div>
        ))}
        
        {particles.map(particle => (
          <div
            key={particle.id}
            className="particle"
            style={{
              position: 'absolute',
              left: particle.x,
              top: particle.y,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              backgroundColor: particle.color,
              opacity: particle.life,
              pointerEvents: 'none',
              borderRadius: '50%',
              boxShadow: `0 0 ${particle.size}px ${particle.color}`
            }}
          />
        ))}
      </motion.div>
      <motion.div className="skill-description-container">
        <AnimatePresence mode="wait">
          {showPrompt ? (
            <motion.div 
              key="prompt"
              className="description-prompt"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <motion.span 
                className="pulse-circle"
                animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <span className="prompt-text">Cliquez sur un logo pour découvrir la compétence</span>
            </motion.div>
          ) : (
            <motion.div 
              key="description"
              className="skill-description active"
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.9 }}
              transition={{ duration: 0.4, type: "spring" }}
            >
              <motion.div 
                className="description-content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                {activeIdx !== null ? skills[activeIdx].description : ''}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </section>
  );
};

export default SkillsBouncing;
